/**
 * Validation layer for the Arrange-phase engine.
 * 
 * Provides reusable validators that ensure inputs conform to the harmonizer's
 * normalized input contracts. All validation rules are derived from the
 * HarmonizedToolAdapter.inputContract structure.
 */

import type {
  HarmonizedToolAdapter,
  Item,
  CriteriaSet,
  ToolConfig,
  Lens,
} from "../harmonizer/types";

/**
 * Validates that Items satisfy the adapter's input contract requirements.
 * 
 * Checks:
 * - Required Item fields (id, label, etc.) are present
 * - Required attributes exist in each Item's attributes object
 * 
 * @param items - Items to validate
 * @param adapter - Harmonized tool adapter with input contract
 * @throws Error if validation fails
 */
export function validateItemsForTool(
  items: Item[],
  adapter: HarmonizedToolAdapter
): void {
  if (items.length === 0) {
    throw new Error(`Tool '${adapter.toolName}' requires at least one item.`);
  }

  const contract = adapter.inputContract;

  // Validate required Item fields
  const requiredFields = contract.requiredItemFields || [];
  for (const item of items) {
    for (const field of requiredFields) {
      if (field === "id" && !item.id) {
        throw new Error(
          `Item validation failed: missing required field 'id' for tool '${adapter.toolName}'.`
        );
      }
      if (field === "label" && !item.label) {
        throw new Error(
          `Item validation failed: missing required field 'label' for tool '${adapter.toolName}'.`
        );
      }
      if (field === "description" && item.description === undefined) {
        // Description is optional in the type, but if it's in requiredItemFields,
        // we expect it to be explicitly set (even if empty string)
        // We allow undefined since it's optional in the type system
      }
    }

    // Validate required attributes
    const requiredAttrs = contract.requiredAttributes || [];
    for (const attrName of requiredAttrs) {
      if (!(attrName in item.attributes)) {
        throw new Error(
          `Item validation failed: item '${item.id}' missing required attribute '${attrName}' for tool '${adapter.toolName}'.`
        );
      }

      // Check that attribute value is not null/undefined
      const value = item.attributes[attrName];
      if (value === null || value === undefined) {
        throw new Error(
          `Item validation failed: item '${item.id}' has null/undefined value for required attribute '${attrName}' for tool '${adapter.toolName}'.`
        );
      }
    }
  }
}

/**
 * Validates that CriteriaSet satisfies the adapter's criteria contract.
 * 
 * Checks:
 * - CriteriaSet is provided if required
 * - Criteria IDs match allowed criteria IDs (if specified)
 * 
 * @param criteriaSet - CriteriaSet to validate (may be null)
 * @param adapter - Harmonized tool adapter with input contract
 * @throws Error if validation fails
 */
export function validateCriteriaSetForTool(
  criteriaSet: CriteriaSet | null,
  adapter: HarmonizedToolAdapter
): void {
  const contract = adapter.inputContract.criteriaSetContract;

  // Check if explicit CriteriaSet is required
  if (contract.requiresExplicitCriteriaSet && !criteriaSet) {
    throw new Error(
      `Tool '${adapter.toolName}' requires an explicit CriteriaSet, but none was provided.`
    );
  }

  // If CriteriaSet is provided, validate against allowed criteria IDs
  if (criteriaSet && contract.allowedCriteriaIds.length > 0) {
    const providedCriteriaIds = criteriaSet.criteria.map((c) => c.id);
    const allowedIds = contract.allowedCriteriaIds;

    // Check if all provided criteria IDs are in the allowed list
    for (const criteriaId of providedCriteriaIds) {
      if (!allowedIds.includes(criteriaId)) {
        throw new Error(
          `CriteriaSet validation failed: criteria ID '${criteriaId}' is not allowed for tool '${adapter.toolName}'. Allowed IDs: ${allowedIds.join(", ")}.`
        );
      }
    }
  }
}

/**
 * Validates that ToolConfig conforms to the adapter's toolConfigFields specification.
 * 
 * Checks:
 * - Required fields are present
 * - Field types match (enum, numeric, integer, boolean, string)
 * - Enum values are within allowed values
 * - Numeric values are within min/max bounds
 * 
 * @param toolConfig - ToolConfig to validate
 * @param adapter - Harmonized tool adapter with input contract
 * @throws Error if validation fails
 */
export function validateToolConfigForTool(
  toolConfig: ToolConfig,
  adapter: HarmonizedToolAdapter
): void {
  const fields = adapter.inputContract.toolConfigFields || [];

  for (const field of fields) {
    const value = toolConfig[field.name];

    // Check required fields (if no default is provided, field is required)
    if (value === undefined || value === null) {
      if (field.default === undefined) {
        throw new Error(
          `ToolConfig validation failed: missing required field '${field.name}' for tool '${adapter.toolName}'.`
        );
      }
      // Field has default, so it's optional - skip validation
      continue;
    }

    // Validate type
    switch (field.type) {
      case "enum":
        if (!field.allowedValues || field.allowedValues.length === 0) {
          throw new Error(
            `ToolConfig validation failed: field '${field.name}' is of type 'enum' but has no allowedValues defined for tool '${adapter.toolName}'.`
          );
        }
        if (!field.allowedValues.includes(value)) {
          throw new Error(
            `ToolConfig validation failed: field '${field.name}' has value '${value}' which is not in allowed values [${field.allowedValues.join(", ")}] for tool '${adapter.toolName}'.`
          );
        }
        break;

      case "numeric":
      case "integer":
        if (typeof value !== "number") {
          throw new Error(
            `ToolConfig validation failed: field '${field.name}' must be a number for tool '${adapter.toolName}', got ${typeof value}.`
          );
        }
        if (field.min !== undefined && value < field.min) {
          throw new Error(
            `ToolConfig validation failed: field '${field.name}' value ${value} is below minimum ${field.min} for tool '${adapter.toolName}'.`
          );
        }
        if (field.max !== undefined && value > field.max) {
          throw new Error(
            `ToolConfig validation failed: field '${field.name}' value ${value} is above maximum ${field.max} for tool '${adapter.toolName}'.`
          );
        }
        if (field.type === "integer" && !Number.isInteger(value)) {
          throw new Error(
            `ToolConfig validation failed: field '${field.name}' must be an integer for tool '${adapter.toolName}', got ${value}.`
          );
        }
        break;

      case "boolean":
        if (typeof value !== "boolean") {
          throw new Error(
            `ToolConfig validation failed: field '${field.name}' must be a boolean for tool '${adapter.toolName}', got ${typeof value}.`
          );
        }
        break;

      case "string":
        if (typeof value !== "string") {
          throw new Error(
            `ToolConfig validation failed: field '${field.name}' must be a string for tool '${adapter.toolName}', got ${typeof value}.`
          );
        }
        break;
    }
  }
}

/**
 * Applies a Lens filter to Items, returning only items that match all filter conditions.
 * 
 * Supports all LensFilterOp operations:
 * - eq, neq: equality/inequality
 * - gt, gte, lt, lte: numeric comparisons
 * - in, not_in: array membership
 * 
 * @param items - Items to filter
 * @param lens - Lens with filters to apply
 * @returns Filtered Items array
 */
export function applyLens(items: Item[], lens: Lens | null): Item[] {
  if (!lens) {
    return items;
  }

  if (!lens.filters || lens.filters.length === 0) {
    return items;
  }

  let filtered = items;

  // Apply all filters (AND logic)
  for (const filter of lens.filters) {
    filtered = filtered.filter((item) => {
      const attrValue = item.attributes[filter.attribute];

      // Handle null/undefined attribute values
      if (attrValue === null || attrValue === undefined) {
        // For most operations, null/undefined doesn't match
        if (filter.op === "eq" && filter.value === null) {
          return true;
        }
        if (filter.op === "neq" && filter.value !== null) {
          return true;
        }
        return false;
      }

      switch (filter.op) {
        case "eq":
          return attrValue === filter.value;

        case "neq":
          return attrValue !== filter.value;

        case "gt":
          if (typeof attrValue !== "number" || typeof filter.value !== "number") {
            return false;
          }
          return attrValue > filter.value;

        case "gte":
          if (typeof attrValue !== "number" || typeof filter.value !== "number") {
            return false;
          }
          return attrValue >= filter.value;

        case "lt":
          if (typeof attrValue !== "number" || typeof filter.value !== "number") {
            return false;
          }
          return attrValue < filter.value;

        case "lte":
          if (typeof attrValue !== "number" || typeof filter.value !== "number") {
            return false;
          }
          return attrValue <= filter.value;

        case "in":
          if (!Array.isArray(filter.value)) {
            return false;
          }
          return filter.value.includes(attrValue);

        case "not_in":
          if (!Array.isArray(filter.value)) {
            return true;
          }
          return !filter.value.includes(attrValue);

        default:
          // Unknown operation - exclude item for safety
          return false;
      }
    });
  }

  // Apply sorting if specified
  if (lens.sort) {
    filtered = [...filtered].sort((a, b) => {
      const { attribute, direction } = lens.sort!;
      const dir = direction === "asc" ? 1 : -1;

      if (attribute === "_score" || attribute === "_rank") {
        // These are special attributes that would come from scores
        // For now, we can't sort by them without scores - skip sorting
        return 0;
      }

      const aVal = a.attributes[attribute];
      const bVal = b.attributes[attribute];

      if (aVal === null || aVal === undefined) {
        return 1;
      }
      if (bVal === null || bVal === undefined) {
        return -1;
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return (aVal - bVal) * dir;
      }

      // String comparison
      return String(aVal).localeCompare(String(bVal)) * dir;
    });
  }

  return filtered;
}

